import { ReadingDataPoint, PvDataPoint, TimeFilter } from '../types';

/**
 * Generates a complete time series for a given filter, filling missing periods with zero values.
 *
 * - TODAY:      hours from 00:00 up to the current hour
 * - THIS_WEEK:  days from Monday up to today
 * - THIS_MONTH: days from 1st up to today
 * - LAST_MONTH: all days of the previous month
 * - THIS_YEAR:  months from January up to the current month
 * - LAST_YEAR:  all 12 months
 */

function toISOLocal(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function generatePeriods(filter: TimeFilter): string[] {
  const now = new Date();
  const periods: string[] = [];

  switch (filter) {
    case 'TODAY': {
      const currentHour = now.getHours();
      for (let h = 0; h <= currentHour; h++) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, 0, 0);
        periods.push(toISOLocal(d));
      }
      break;
    }
    case 'THIS_WEEK': {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset);
      for (let i = 0; i <= mondayOffset; i++) {
        const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
        if (d > today) break;
        periods.push(toISOLocal(d));
      }
      break;
    }
    case 'THIS_MONTH': {
      const todayDate = now.getDate();
      for (let day = 1; day <= todayDate; day++) {
        const d = new Date(now.getFullYear(), now.getMonth(), day, 0, 0, 0);
        periods.push(toISOLocal(d));
      }
      break;
    }
    case 'LAST_MONTH': {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const daysInLastMonth = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
      for (let day = 1; day <= daysInLastMonth; day++) {
        const d = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), day, 0, 0, 0);
        periods.push(toISOLocal(d));
      }
      break;
    }
    case 'THIS_YEAR': {
      const currentMonth = now.getMonth();
      for (let m = 0; m <= currentMonth; m++) {
        const d = new Date(now.getFullYear(), m, 1, 0, 0, 0);
        periods.push(toISOLocal(d));
      }
      break;
    }
    case 'LAST_YEAR': {
      const lastYear = now.getFullYear() - 1;
      for (let m = 0; m < 12; m++) {
        const d = new Date(lastYear, m, 1, 0, 0, 0);
        periods.push(toISOLocal(d));
      }
      break;
    }
  }

  return periods;
}

function periodKey(period: string, filter: TimeFilter): string {
  // Normalize period strings to a comparable key based on granularity
  const d = new Date(period);
  switch (filter) {
    case 'TODAY':
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
    case 'THIS_WEEK':
    case 'THIS_MONTH':
    case 'LAST_MONTH':
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    case 'THIS_YEAR':
    case 'LAST_YEAR':
      return `${d.getFullYear()}-${d.getMonth()}`;
  }
}

export function fillReadingTimeSeries(
  data: ReadingDataPoint[],
  filter: TimeFilter,
): ReadingDataPoint[] {
  const lookup = new Map<string, ReadingDataPoint>();
  for (const point of data) {
    lookup.set(periodKey(point.period, filter), point);
  }

  const allPeriods = generatePeriods(filter);
  return allPeriods.map(period => {
    const key = periodKey(period, filter);
    const existing = lookup.get(key);
    return existing ?? { period, value: 0 };
  });
}

export function fillPvTimeSeries(
  data: PvDataPoint[],
  filter: TimeFilter,
): PvDataPoint[] {
  const lookup = new Map<string, PvDataPoint>();
  for (const point of data) {
    lookup.set(periodKey(point.period, filter), point);
  }

  const allPeriods = generatePeriods(filter);
  return allPeriods.map(period => {
    const key = periodKey(period, filter);
    const existing = lookup.get(key);
    return existing ?? { period, productionKwh: 0, feedInKwh: 0, selfConsumptionKwh: 0 };
  });
}
